import { cachedAPIWrapper } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";
import { TestimonialHelperIdb } from "../testimonial/helperIdb";

/**
 * windsurf generated code start
 * PreFetchUrls
 *
 * Provides utilities to construct and prefetch API URLs by resolving dynamic
 * placeholders from the current web context (origin, IDs, DBA name) and
 * configured endpoints (`apiConfig`). Implements a singleton to avoid
 * redundant environment reads and to centralize URL assembly logic.
 *
 * Security & constraints
 * - Does not expose sensitive information; values are sourced from DOM/storage
 *   and static config.
 * - Intended for browser environments (uses window/document/storage APIs).
 * - Defensive logging is used for unsupported/empty segments.
 *
 * Key methods
 * - `constructUrls()`: Resolves placeholders to a concrete URL path.
 * - `getPreFetchUrls()`: Validates and prefetches a list of URLs.
 * - `loadApisOnIdle()`: Fetch wrapper with basic validation and error isolation.
 *
 * windsurf generated code end
 */
export class PreFetchUrls {
    private static instance: PreFetchUrls;
    private readonly conceptId: string;
    private readonly countryId: string;
    private readonly origin: string;
    private readonly weblocationId: string;
    private readonly dbaName: string;
    private readonly urlMappings = new Map<string, () => string>([
        ['{conceptid}', () => this.conceptId],
        ['{weblocationid}', () => this.weblocationId || '0'],
        ['{franchiseweblocationid}', () => this.weblocationId || '0'],
        ['{countryid}', () => this.countryId],
        ['{origin}', () => this.origin],
        ['{dbaname}', () => this.dbaName],
        // API Config mappings
        ['{apiconfig.locatelocationapiwithroundrobinfalse}', () => apiConfig.LocateLocationApiWithRoundRobinFalse],
        ['{apiconfig.locatelocationapiwithroundrobintrue}', () => apiConfig.LocateLocationApiWithRoundRobinTrue],
        ['{apiconfig.locatelocationapiwithroundrobin}', () => apiConfig.LocateLocationApiWithRoundRobin],
        ['{apiconfig.app_link_send}', () => apiConfig.APP_LINK_SEND],
        ['{apiconfig.footer_brand_link}', () => apiConfig.FOOTER_BRAND_LINK],
        ['{apiconfig.stateapi}', () => apiConfig.stateApi],
        ['{apiconfig.contact_us_send}', () => apiConfig.CONTACT_US_SEND],
        ['{apiconfig.booking_api_url}', () => apiConfig.BOOKING_API_URL],
        ['{apiconfig.testimonial_reviews}', () => apiConfig.TESTIMONIAL_REVIEWS],
        ['{apiconfig.ourteam}', () => apiConfig.ourTeam],
        ['{apiconfig.getheadermenu}', () => apiConfig.GETHeaderMenu],
        ['{apiconfig.fullattribute}', () => apiConfig.fullAttribute],
        ['{apiconfig.howtoknow}', () => apiConfig.howToKnow],
        ['{apiconfig.franchiseweblocationpostalcodefees}', () => apiConfig.franchiseWebLocationPostalCodeFees],
        ['{apiconfig.appointmentapi}', () => apiConfig.AppointMentAPi],
        ['{apiconfig.get_attribute_data}', () => apiConfig.GET_ATTRIBUTE_DATA],
        ['{apiconfig.get_service_bundle}', () => apiConfig.GET_SERVICE_BUNDLE],
        ['{apiconfig.hms_booking}', () => apiConfig.HMS_BOOKING],
        ['{apiconfig.blog_url}', () => apiConfig.BLOG_URL],
        ['{apiconfig.franchiselookupbyserviceaddress}', () => apiConfig.FranchiseLookUpByServiceAddress],
        ['{apiconfig.paymentposturl}', () => apiConfig.paymentPostUrl],
        ['{apiconfig.poslookup}', () => apiConfig.POSLookup],
        ['{apiconfig.synccleoLeadsources}', () => apiConfig.syncCleoLeadSources],
        ['{apiconfig.discover_api}', () => apiConfig.DISCOVER_API],
        ['{apiconfig.calculateestimate}', () => apiConfig.calculateEstimate],
        ['{apiconfig.franchiseposlookup}', () => apiConfig.FranchisePOSLookup],
        ['{apiconfig.weblocationsapi}', () => apiConfig.WebLocationsAPI],
        ['{apiconfig.weblocationsfranchisegroupapi}', () => apiConfig.WebLocationsFranchiseGroupAPI],
        ['{apiconfig.geocodesearchapiurl}', () => apiConfig.geoCodeSearchApiUrl],
        ['{apiconfig.genericleadsourceendpoint}', () => apiConfig.GenericLeadSourceEndpoint],
        ['{apiconfig.mollyleadsourceendpoint}', () => apiConfig.MollyLeadSourceEndpoint],
        ['{apiconfig.apilogerrorendpoint}', () => apiConfig.ApiLogerrorEndpoint],
        ['{apiconfig.apilogperformanceendpoint}', () => apiConfig.ApiLogperformanceEndpoint],
        ['{apiconfig.franchiseterritoriesapi}', () => apiConfig.FranchiseTerritoriesAPI],
        ['{apiconfig.photosapi}', () => apiConfig.PhotosAPI],
        ['{apiconfig.photocategoriesapi}', () => apiConfig.PhotoCategoriesAPI],
        ['{apiconfig.crownpeak_weblocation_url}', () => apiConfig.CROWNPEAK_WEBLOCATION_URL],
        ['{apiconfig.crownpeak_caching_settings}', () => apiConfig.CROWNPEAK_CACHING_SETTINGS],
        ['{apiconfig.vin_api_url}', () => apiConfig.VIN_API_URL],
        ['{apiconfig.attom_api_url}', () => apiConfig.ATTOM_API_URL],
        ['{apiconfig.quote_api_url}', () => apiConfig.QUOTE_API_URL],
        ['{apiconfig.client_transform_carousel_v2}', () => apiConfig.CLIENT_TRANSFORM_CAROUSEL_V2]
    ]);

    /**
     * windsurf generated code start
     * Returns the singleton instance of `PreFetchUrls`.
     * Ensures a single source of truth for environment-derived values.
     *
     * Returns
     * - A shared `PreFetchUrls` instance.
     *
     * windsurf generated code end
     */
    static getInstance(): PreFetchUrls {
        if (!PreFetchUrls.instance) {
            PreFetchUrls.instance = new PreFetchUrls();
        }
        return PreFetchUrls.instance;
    }
    
    /**
     * windsurf generated code start
     * Initializes environment-dependent fields.
     *
     * Notes
     * - Reads from DOM and Web Storage; runs in browser context.
     * - Avoids heavy/async operations to keep construction cheap.
     *
     * windsurf generated code end
     */
    constructor() {
        // Read initial environment/context values once to avoid repeated DOM/storage access
        this.conceptId = (document.getElementById("conceptId") as HTMLInputElement)?.value ?? "";
        this.countryId = this.determineCountryId();
        this.origin = this.insertOrigin();
        this.weblocationId = this.getWebLocationId() ?? "0"; // default to "0" if not present
        this.dbaName = this.getDBAName();
    }
    
    /**
     * windsurf generated code start
     * Loads the given URL via the cached API wrapper, logging success/failure.
     *
     * Parameters
     * - url: string — Target URL. Must be non-empty; trailing slashes are trimmed.
     *
     * Behavior
     * - Validates input, then calls `cachedAPIWrapper(url, 120, false)`.
     * - Errors are caught and logged; does not throw to avoid interrupting batches.
     *
     * windsurf generated code end
     */
    async loadApisOnIdle(url: string): Promise<void> {
        try {
            // Validate URL format
            if (!url || typeof url !== 'string' || url.trim().length === 0) {
                console.warn('Invalid URL provided to loadApisOnIdle:', url);
                return;
            }
            
            // Trim trailing slashes to normalize before caching/fetching
            await cachedAPIWrapper(url.replace(/\/+$/, ''), 120, false);
        } catch (error) {
            // Isolate failures so one bad URL doesn't block others
            console.error(`Failed to load ${url}:`, error);
        }
    }
    
    /**
     * windsurf generated code start
     * Validates, resolves, and prefetches a list of Crownpeak-provided URLs.
     *
     * Parameters
     * - urlsFromCP: string[] — Raw URL patterns that may contain placeholders.
     *
     * Behavior
     * - Resolves each entry via `constructUrls()`; validates using the URL constructor.
     * - Skips invalid/empty entries. If none valid, logs and exits.
     * - Prefetches valid URLs concurrently via `loadApisOnIdle()` and logs a summary.
     * - Any thrown error is caught and logged without leaking sensitive data.
     *
     * windsurf generated code end
     */
    async getPreFetchUrls(urlsFromCP: string[]): Promise<void> {
        try {
            // Guard: nothing to process or invalid input
            if (!urlsFromCP || urlsFromCP.length === 0 || !Array.isArray(urlsFromCP)) return;
            
            // Filter out invalid URLs and process valid ones
            const validUrls = urlsFromCP
                .map((url): string | null => {
                    // Skip non-strings/empty entries early
                    if (typeof url !== 'string' || url.trim().length === 0) return null;
                    // Resolve any placeholders like {origin}, {weblocationid}, {apiconfig.*}
                    let dynamicUrl = this.constructUrls(url);
                    if (!dynamicUrl) return null;
                    try {
                        // Use the URL constructor as a validity check
                        new URL(dynamicUrl);
                        return dynamicUrl;
                    } catch {
                        return null;
                    }
                })
                .filter((url): url is string => url !== null);
            
            if (validUrls.length === 0) {
                console.log('No valid URLs found in preFetchList');
                return;
            }
            
            const results = await Promise.allSettled(validUrls.map((url: string) => this.loadApisOnIdle(url)));
            
            // Log summary of results
            const successful = results.filter(result => result.status === 'fulfilled').length;
            const failed = results.filter(result => result.status === 'rejected').length;
            
            console.log(`Prefetch completed: ${successful} successful, ${failed} failed out of ${validUrls.length} URLs`);
            
            // Log any rejected promises for debugging
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.error(`Failed to prefetch ${validUrls[index]}:`, result.reason);
                }
            });
            
        } catch (error) {
            // Final catch to prevent unhandled errors from bubbling up
            console.error('Error in getPreFetchUrls:', error);
        }
    }
    
    /**
     * windsurf generated code start
     * Retrieves the franchise web location ID from localStorage or DOM.
     *
     * Resolution order
     * 1) localStorage.franchiseWebLocationId
     * 2) value of element with id "weblocationId"
     * 3) Fallback: "0"
     *
     * Returns
     * - A string web location id, defaulting to "0" if missing.
     *
     * windsurf generated code end
     */
    getWebLocationId(): string {
        // Prefer persisted value; fallback to DOM field; default to "0"
        return localStorage.getItem("franchiseWebLocationId") || 
               (document.getElementById('weblocationId') as HTMLInputElement)?.value || '0';
    }
    
    /**
     * windsurf generated code start
     * Retrieves the origin component of the current browser location.
     *
     * Purpose
     * - Provides a consistent base URL (scheme + host + optional port), e.g.,
     *   "https://example.com" or "http://localhost:8080".
     * - Used by `PreFetchUrls.constructUrls()` when resolving the `{origin}` placeholder.
     *
     * Behavior and constraints
     * - Browser-only: relies on `window.location.origin` and will not work in SSR/non-DOM environments.
     * - No normalization: returns the origin exactly as provided by the browser.
     * - No trailing slash: origin strings never include a trailing slash by spec.
     * - Security: does not expose sensitive data; it simply mirrors the current location origin.
     *
     * Returns
     * - The origin string for the current page.
     *
     * Example
     * ```ts
     * const pf = PreFetchUrls.getInstance();
     * const base = pf.insertOrigin(); // e.g., "https://brand.example.com"
     * const url = `${base}/api/v1/health`;
     * ```
     *
     * See also
     * - `PreFetchUrls.constructUrls()` for dynamic URL assembly using `{origin}`.
     *
     * windsurf generated code end
     */
    insertOrigin(): string {
        // Use the browser-provided origin (scheme + host + optional port)
        const origin = window.location.origin;
        return origin;
    }

    /**
     * windsurf generated code start
     * Retrieves the DBA (Doing Business As) name.
     *
     * Resolution order
     * 1) value of element with id "dbaName"
     * 2) localStorage.dbaName
     * 3) Fallback: empty string
     *
     * Returns
     * - DBA display name string (may be empty).
     *
     * windsurf generated code end
     */
    getDBAName(): string {
        // Prefer DOM field; fallback to localStorage; allow empty string if not set
        return (document.getElementById("dbaName") as HTMLInputElement)?.value ?? localStorage.getItem("dbaName") ?? "";
    }
    
    /**
     * windsurf generated code start
     * Maps a country code to a numeric country identifier.
     *
     * Resolution
     * - Reads `countryCode` from sessionStorage or the element with id "countryCode".
     * - Returns "1" for US, otherwise "2".
     *
     * Returns
     * - "1" | "2"
     *
     * windsurf generated code end
     */
    determineCountryId(): string {
        // Resolve country code from session or DOM and map to numeric ID
        const countryCode = sessionStorage.getItem('countryCode') || 
                           (document.getElementById("countryCode") as HTMLInputElement)?.value || '';
        return countryCode.toLowerCase() === "us" ? "1" : "2";
    }

    /**
     * windsurf generated code start
     * Resolves a dynamic URL pattern by replacing placeholders with runtime values.
     *
     * Parameters
     * - inputUrl: string — Pattern that may include a "[js]" directive and placeholders like
     *   `{origin}`, `{weblocationid}`, `{franchiseweblocationid}`, `{countryid}`, `{conceptid}`, `{dbaname}`
     *   and a set of `{apiconfig.*}` keys mapped from `apiConfig`.
     *
     * Behavior
     * - If the pattern does not include "[js]", returns the original input unchanged.
     * - After "[js]", segments separated by '/' are processed:
     *   - Literal segments are copied as-is.
     *   - Placeholder segments are looked up in `urlMappings` and replaced.
     * - If a placeholder is unknown or resolves to an empty value, logs a warning and returns null.
     * - Returns a trailing slash-terminated URL path (no protocol/host unless `{origin}` is included).
     *
     * Returns
     * - A constructed URL string ending with '/', or null on validation failure.
     *
     * Examples
     * - "[js]{origin}/{conceptid}/" -> "https://example.com/55/"
     * - "[js]{apiconfig.blog_url}/posts" -> "https://api.example.com/blog/posts/"
     * - "[JS]{apiConfig.TESTIMONIAL_REVIEWS}/{conceptId}/{weblocationId}/{countryId}/0/1/30/5" -> Review API
     *
     * windsurf generated code end
     */
    constructUrls(inputUrl: string): string | null {
        // Normalize to simplify placeholder matching (placeholders are stored lowercase)
        const normalizedUrl = inputUrl.toLowerCase();
        if (!normalizedUrl.includes('[js]')) {
            // No dynamic directive, return as-is
            return inputUrl;
        }

        // Everything after [js] is a path of '/'-separated segments
        const segments = normalizedUrl.split('[js]')[1]?.split('/');
        /* istanbul ignore next */
        if (!segments?.length) {
            return null;
        }

        // Pre-scan segments for testimonial API placeholder and set flag once
        if (segments.some(seg => seg.includes('{apiconfig.testimonial_reviews}'))) {
            sessionStorage.setItem('rocketEnabled', 'true');
            sessionStorage.removeItem('rocketDisabled');
            const instance = TestimonialHelperIdb.getInstance();
            instance.domLookup();
            instance.getData();
        }else{
            sessionStorage.setItem('rocketDisabled', 'true');
            sessionStorage.removeItem('rocketEnabled');
        }

        const urlParts: string[] = [];
        
        for (const segment of segments) {
            // Copy literal segments as-is; process placeholders via the mapping
            if (!segment.includes('{')) {
                urlParts.push(segment);
            } else {
                const normalizedSegment = segment.trim();
                const getValue = this.urlMappings.get(normalizedSegment);
                
                if (getValue) {
                    // Evaluate mapping lazily to avoid unnecessary calls
                    const value = getValue();
                    if (value) {
                        urlParts.push(value);
                    } else {
                        // Explicitly reject empty placeholder values
                        console.warn(`Empty value for segment: ${segment}`);
                        return null;
                    }
                } else {
                    // Unknown placeholder key
                    console.warn(`Unsupported segment: ${segment}`);
                    return null;
                }
            }
        }

        // Join back together and ensure a trailing slash for consistency
        const constructedUrl = urlParts.join('/') + '/';
        return constructedUrl;
    }
}
